import {
  BugOutlined,
  CalendarOutlined,
  CopyOutlined,
  CreditCardOutlined,
  InfoCircleOutlined,
  MailOutlined,
  MessageOutlined,
  PhoneOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  ConfigProvider,
  Descriptions,
  Drawer,
  Flex,
  Form,
  Input,
  List,
  Modal,
  message,
  Popconfirm,
  Radio,
  Skeleton,
  Space,
  Timeline,
  Tooltip,
  Typography,
} from 'antd';
import {
  approveOrder,
  estimateOrder,
  getOrderDetailByOrderId,
  isRideServiceAvailable,
  pushOrderToPosHub,
  repushOrderToPosHub,
  updateOrderState,
} from 'api/orders';
import FormItem from 'components/FormItem';
import JumpLink from 'components/JumpLink';
import withGuard from 'components/withGuard';
import { AdminContext } from 'constants/contexts';
import { PERMISSIONS } from 'constants/permissions';
import Header from 'components/OrderOverlay/Header';
import {
  getItemValue,
  isEzCaterOrder,
  isOmniPartner,
  isFoodHubSelfDelivery,
} from 'utils/order';
import dayjs from 'dayjs';
import { useHasAnyOfPermissions } from 'hooks/useHasPermission';
import useIsDesktop from 'hooks/useIsDesktop';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import { captureException } from 'utils/errors';
import http, { getHttpErrorMessage } from 'utils/http';
import { renderServingSizeLabel } from 'utils/orders';
import TimeDialogue from 'containers/Order/components/TimeDialogue';
import {
  getBlobHttpErrorMessage,
  openBlankPrintTab,
  openPdfResponseInTab,
} from 'containers/PrepKitchen/kitchenSheetDownload';
import { Check } from '@mui/icons-material';
import { checkExistDispatcher, createDispatcherJob } from './requests';
import ChangeLog from './ChangeLog';
import DispatcherDebugPanel from './DispatcherDebugPanel';
import { PaymentTypes, RIDE_STATUS, WL_CONFIG } from '../../constants';
import {
  capitalizeMerchantType,
  formatAmount,
  formatPhoneNumber,
  getData,
} from '../../utils';
import { formatPosTime, queuedPosDetailMessage } from '../../utils/posHold';
import * as orderStatus from '../../constants/status';
import Row from './Row';

function OrderOverlay({
  orderId: orderIdParam,
  data,
  handleOrderUpdate,
  onClose: handleClose,
  disableActions,
}) {
  const { orderId = orderIdParam } = useParams();
  const [orderDetail, setOrderDetail] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const order = useMemo(
    () => ({
      ...data,
      ...(+orderDetail.id === +orderId ? orderDetail : {}),
    }),
    [data, orderDetail, orderId],
  );
  const [timeEST, setTimeEST] = useState('');
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [timeConfirmLoading, setTimeConfirmLoading] = useState(false);
  const [orderTime, setOrderTime] = useState(order.estimate);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [readyLoading, setReadyLoading] = useState(false);
  const [deliverLoading, setDeliverLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [posHubLoading, setPosHubLoading] = useState(false);
  const [posHubOrderId, setPosHubOrderId] = useState(null);
  /** True after push/repush succeeds until API detail shows poshub_order_id (async Celery create). */
  const [posHubAwaitingIntegration, setPosHubAwaitingIntegration] = useState(false);
  const [repushModalOpen, setRepushModalOpen] = useState(false);
  const [repushReason, setRepushReason] = useState('');
  const [repushLoading, setRepushLoading] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteLoading, setNoteLoading] = useState(false);
  const [selectedEstimateOption, setSelectedEstimateOption] = useState();
  const [estimatedPickupTs, setEstimatedPickupTs] = useState(null);
  const [canRequestDispatcher, setCanRequestDispatcher] = useState(false);
  const [debugPanelOpen, setDebugPanelOpen] = useState(false);
  const [dispatcherError, setDispatcherError] = useState(null);
  const [handoffSheetLoading, setHandoffSheetLoading] = useState(false);
  const isAdmin = useHasAnyOfPermissions([PERMISSIONS.ADMIN_ACCESS]);
  const canOpenPrepSheet = useHasAnyOfPermissions([PERMISSIONS.GLOBAL_MENU]);
  const { organizationId } = useContext(AdminContext);

  const isScheduled = Boolean(order.scheduled_for);
  const isEzCaterOrderView = isEzCaterOrder(order);
  const isFoodHubSelfDeliveryOrder = isFoodHubSelfDelivery(order);
  const isFoodHubSelfDeliveryOrOmniPartner =
    isFoodHubSelfDeliveryOrder || isOmniPartner(order);
  const actionsDisabled = disableActions || isEzCaterOrderView;
  const { message, modal } = App.useApp();
  const open = Boolean(orderId);

  const fetchHandoffSheet = useCallback(() => http.get(
    `v1/orders/${order.id}/handoff-sheet/`,
    {
      params: { tz: Intl.DateTimeFormat().resolvedOptions().timeZone },
      responseType: 'blob',
    },
  ), [order.id]);

  const handleHandoffSheet = useCallback(async () => {
    if (!order.id || handoffSheetLoading) {
      return;
    }
    const printTab = openBlankPrintTab();
    setHandoffSheetLoading(true);
    try {
      const response = await fetchHandoffSheet();
      if (!openPdfResponseInTab(response, printTab)) {
        message.warning('Allow pop-ups for this site to open the handoff sheet, or try again.');
      }
    } catch (error) {
      printTab?.close();
      const errorMessage = await getBlobHttpErrorMessage(error);
      message.error(errorMessage || 'Unable to generate the handoff sheet');
    } finally {
      setHandoffSheetLoading(false);
    }
  }, [fetchHandoffSheet, handoffSheetLoading, message, order.id]);

  const openPrepSheetForOrder = useCallback(() => {
    if (!order.id) {
      return;
    }
    if (!canOpenPrepSheet) {
      message.error("You don't have access to Prep Sheets. Ask an admin for Prep & Kitchen access.");
      return;
    }
    navigate(`/console/prep-kitchen/prep-sheet?order_id=${order.id}`);
  }, [canOpenPrepSheet, message, navigate, order.id]);

  useEffect(() => {
    const prevValue = document.body.style.overflow;
    if (open) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = prevValue;
    };
  }, [open]);

  const handleEditOrder = (id) => {
    navigate(`/console/orders/${id}/edit`);
  };

  function onClose() {
    handleClose ? handleClose() : navigate('..');
  }
  const estimateOptions = useMemo(
    () => [
      { id: 0, title: '15 minutes', time: 15 },
      { id: 1, title: '25 minutes', time: 25 },
      { id: 2, title: '45 minutes', time: 45 },
      {
        id: -1,
        title: <CustomEstInput value={timeEST} onChange={setTimeEST} />,
        time: timeEST,
      },
    ],
    [timeEST],
  );

  const deliveryEstimateOptions = useMemo(
    () => {
      const nextAvailablePickup = Math.max(1, dayjs(estimatedPickupTs).diff(dayjs(), 'minute'));
      return [
        {
          id: 0,
          title: `Next Available Pickup (${nextAvailablePickup} mins)`,
          time: nextAvailablePickup,
        },
        {
          id: -1,
          title: <CustomEstInput value={timeEST} onChange={setTimeEST} />,
          time: timeEST,
        },
      ];
    },
    [timeEST, estimatedPickupTs],
  );

  const isAcceptingDisabled = () => {
    if (isEzCaterOrderView) {
      return true;
    }
    if (isFoodHubSelfDeliveryOrder) {
      return true;
    }
    if (isOmniPartner(order)) {
      return true;
    }
    /** An estimate must be selected */
    if (isScheduled) {
      return false;
    }
    if (order.has_delivery && !estimatedPickupTs) {
      return true;
    }

    return selectedEstimateOption?.id === -1
      ? !timeEST
      : !selectedEstimateOption?.time;
  };
  const EDIT_LOCKED_MESSAGE_DEFAULT =
    'Order cannot be edited after pickup. Contact support for manual adjustments.';
  const EDIT_LOCKED_MESSAGE_POS =
    'This order was sent to the POS. Item changes in Nibble do not update the kitchen.';

  const posTicketActive =
    Boolean(order.is_poshub_restaurant) &&
    Boolean(order.poshub_order_id ?? posHubOrderId);

  const showPosImmutableWarning =
    posTicketActive &&
    order.state !== orderStatus.REJECTED_BY_RESTAURANT &&
    order.state !== orderStatus.COMPLETED &&
    order.state !== orderStatus.CANCELLED_BY_RESTAURANT;

  const editOrderDisabledTooltip = order.is_editable
    ? null
    : showPosImmutableWarning
      ? EDIT_LOCKED_MESSAGE_POS
      : EDIT_LOCKED_MESSAGE_DEFAULT;

  const posHubButtonDisabled =
    Boolean(posHubOrderId) || posHubLoading || posHubAwaitingIntegration;
  const posHubButtonLabel = order.pos_dispatch_status === 'failed' ? 'Retry send to POS' : 'Push to POS';
  const posHubButton = order.is_poshub_restaurant ? (
    <Tooltip
      title={
        posHubOrderId
          ? 'Already placed on POS'
          : order.pos_dispatch_status === 'queued'
            ? 'Send to POS now'
            : order.pos_dispatch_status === 'failed'
              ? 'Retry send to POS'
              : posHubAwaitingIntegration
                ? 'POS link is being created…'
                : null
      }
    >
      <span>
        <Button
          onClick={() => handlePushToPosHub()}
          loading={posHubLoading || posHubAwaitingIntegration}
          disabled={posHubButtonDisabled}
        >
          {posHubButtonLabel}
        </Button>
      </span>
    </Tooltip>
  ) : null;
  const editOrderButton = (
    <Tooltip title={editOrderDisabledTooltip}>
      <span>
        <Button
          hidden={!isAdmin}
          disabled={!order.is_editable}
          onClick={() => {
            if (order.is_editable) handleEditOrder(orderId);
          }}
        >
          Edit order
        </Button>
      </span>
    </Tooltip>
  );

  const cancelOrderButton = (
    <Button
      danger
      onClick={() => handleRejectOrder(orderId)}
      loading={rejectLoading}
      hidden={!isAdmin || !order.is_cancellable}
    >
      Cancel order
    </Button>
  );

  const ORDER_DIALOGUE_SETTING = [
    {
      oStatus: [orderStatus.NEW],
      timeSelection: false,
      timeEstSelection: true,
      buttons: (
        <ButtonWrapper editOrderButton={editOrderButton} cancelOrderButton={cancelOrderButton}>
          <Button
            type="primary"
            onClick={() => handleAcceptOrder(orderId)}
            loading={acceptLoading}
            disabled={isAcceptingDisabled()}
            className="ml-0"
          >
            Accept order
          </Button>
        </ButtonWrapper>
      ),
    },
    {
      oStatus: [orderStatus.IN_PROGRESS],
      timeSelection: true,
      timeEstSelection: false,
      buttons: (
        <ButtonWrapper editOrderButton={editOrderButton} cancelOrderButton={cancelOrderButton}>
          {posHubButton}
          {!order.is_catering && (
            <Button
              type="primary"
              onClick={() => handleOrderReady(orderId)}
              loading={readyLoading}
              disabled={isFoodHubSelfDeliveryOrOmniPartner}
              className="ml-0"
            >
              Order ready
            </Button>
          )}
            {(!order?.has_delivery || order?.delivery_type === 'self_delivery') && order?.is_catering && (
            <Button
              type="primary"
              onClick={() => handleOrderDeliver(orderId)}
              loading={deliverLoading}
              className="ml-0"
            >
              Order delivered
            </Button>
          )}
        </ButtonWrapper>
      ),
    },
    {
      oStatus: [orderStatus.READY],
      timeSelection: true,
      timeEstSelection: false,
      buttons: (
        <ButtonWrapper editOrderButton={editOrderButton} cancelOrderButton={cancelOrderButton}>
          {posHubButton}
          {(!order?.has_delivery || order?.delivery_type === 'self_delivery') ? (
            <Button
              type="primary"
              onClick={() => handleOrderDeliver(orderId)}
              loading={deliverLoading}
              className="ml-0"
            >
              Order delivered
            </Button>
          ) : null}
        </ButtonWrapper>
      ),
    },
    {
      oStatus: [orderStatus.ON_THE_WAY],
      timeSelection: true,
      timeEstSelection: false,
      buttons: (
        <ButtonWrapper editOrderButton={editOrderButton} cancelOrderButton={cancelOrderButton}>
          {posHubButton}
        </ButtonWrapper>
      ),
    },
    {
      oStatus: [orderStatus.COMPLETED],
      timeSelection: false,
      timeEstSelection: false,
      buttons: (
        <ButtonWrapper editOrderButton={editOrderButton} cancelOrderButton={cancelOrderButton}>
          {posHubButton}
        </ButtonWrapper>
      ),
    },
    {
      oStatus: [orderStatus.DELIVERED],
      timeSelection: false,
      timeEstSelection: false,
      buttons: (
        <ButtonWrapper editOrderButton={editOrderButton} cancelOrderButton={cancelOrderButton}>
          {posHubButton}
        </ButtonWrapper>
      ),
    },
    {
      oStatus: [orderStatus.CANCELLED_BY_RESTAURANT],
      timeSelection: false,
      timeEstSelection: false,
      buttons: (
        <ButtonWrapper editOrderButton={editOrderButton} />
      ),
    },
    {
      oStatus: [orderStatus.REJECTED_BY_RESTAURANT],
      timeSelection: false,
      timeEstSelection: false,
      buttons: (
        <ButtonWrapper editOrderButton={editOrderButton} cancelOrderButton={cancelOrderButton}>
          <Button
            type="primary"
            onClick={() => setRepushModalOpen(true)}
            disabled={repushLoading}
            loading={repushLoading}
          >
            Re-submit to POS
          </Button>
        </ButtonWrapper>
      ),
    },
    {
      oStatus: [orderStatus.NEEDS_APPROVAL],
      timeSelection: false,
      timeEstSelection: false,
      buttons: (
        <ButtonWrapper editOrderButton={editOrderButton} cancelOrderButton={cancelOrderButton}>
          <Button
            type="primary"
            onClick={() => handleApproveOrder(orderId)}
            loading={approveLoading}
            disabled={approveLoading}
            className="ml-0"
          >
            {order.has_dispatcher_error
              ? 'Check and Approve'
              : 'Approve order'}
          </Button>
        </ButtonWrapper>
      ),
    },
    {
      oStatus: [orderStatus.PAYMENT_SCHEDULED],
      timeSelection: false,
      timeEstSelection: false,
      buttons: (
        <ButtonWrapper editOrderButton={editOrderButton} cancelOrderButton={cancelOrderButton}>
          <Button
            type="primary"
            onClick={() => handleApproveOrder(orderId)}
            loading={approveLoading}
            disabled={approveLoading}
            className="ml-0"
          >
            {order.has_dispatcher_error
              ? 'Check and Approve'
              : 'Approve order'}
          </Button>
        </ButtonWrapper>
      ),
    },
  ];
  const setting = ORDER_DIALOGUE_SETTING.find((s) => s.oStatus.includes(order.state));

  const checkDispatcher = useCallback(() => {
    if (!orderId || !organizationId) {
      return;
    }
    checkExistDispatcher(orderId, organizationId)
      .then((result) => {
        setCanRequestDispatcher(result?.status === 200);
      })
      .catch((e) => {
        captureException(e);
        setCanRequestDispatcher(false);
      });
  }, [orderId, organizationId]);

  useEffect(() => {
    if (!order.id) {
      return;
    }
    if (!order.has_delivery && !selectedEstimateOption) {
      setSelectedEstimateOption(estimateOptions[0]);
    } else {
      checkDispatcher();
    }
  }, [order?.has_delivery, estimateOptions, checkDispatcher, selectedEstimateOption, order?.id]);

  const getOrderDetail = () => {
    if (!order.id) setLoading(true);
    getOrderDetailByOrderId(orderId)
      .then((res) => {
        const detailData = getData(res);
        setOrderDetail(detailData);
        setOrderTime(detailData?.estimate);
      })
      .catch((e) => {
        captureException(e);
        message.error('Something went wrong!');
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (!orderId) {
      setOrderDetail({});
      return;
    }
    setPosHubAwaitingIntegration(false);
    getOrderDetail();
  }, [orderId]);

  useEffect(() => {
    const id = order.poshub_order_id ?? null;
    setPosHubOrderId(id);
    if (id) {
      setPosHubAwaitingIntegration(false);
    }
  }, [order.poshub_order_id]);

  // POS OrderIntegration is often created shortly after accept (async). List rows may omit
  // poshub_order_id; refetch detail a few times so "Push to POS" disables without a full reload.
  useEffect(() => {
    if (!orderId || !order.is_poshub_restaurant || order.poshub_order_id) {
      return undefined;
    }
    const pollStates = [
      orderStatus.NEW,
      orderStatus.IN_PROGRESS,
      orderStatus.READY,
      orderStatus.ON_THE_WAY,
      orderStatus.COMPLETED,
      orderStatus.DELIVERED,
    ];
    if (!pollStates.includes(order.state) && !posHubAwaitingIntegration) {
      return undefined;
    }
    if (order.pos_dispatch_status === 'queued' && !posHubAwaitingIntegration) {
      return undefined;
    }

    const maxAttempts = 6;
    const delayMs = 2000;
    const timeoutIds = [];
    let attempt = 0;
    let cancelled = false;

    const scheduleNext = () => {
      const id = setTimeout(() => {
        if (cancelled) return;
        attempt += 1;
        getOrderDetailByOrderId(orderId)
          .then((res) => {
            if (cancelled) return;
            const detailData = getData(res);
            setOrderDetail(detailData);
            if (detailData?.poshub_order_id) {
              return;
            }
            if (attempt >= maxAttempts) {
              setPosHubAwaitingIntegration(false);
              return;
            }
            scheduleNext();
          })
          .catch(() => {
            if (!cancelled && attempt < maxAttempts) scheduleNext();
          });
      }, delayMs);
      timeoutIds.push(id);
    };

    scheduleNext();

    return () => {
      cancelled = true;
      timeoutIds.forEach(clearTimeout);
    };
  }, [
    orderId,
    order.is_poshub_restaurant,
    order.poshub_order_id,
    order.state,
    order.pos_dispatch_status,
    posHubAwaitingIntegration,
  ]);

  // Fetch dispatcher error details when order has dispatcher error
  useEffect(() => {
    if (order.has_dispatcher_error && order.id) {
      http.get(`v1/orders/${order.id}/dispatcher-logs/`)
        .then((res) => {
          const logs = res.data?.logs || [];
          // Find the most recent error log (logs are ordered by most recent first)
          const errorLog = logs.find((log) => 
            log.error || (log.status_code >= 400) || 
            (log.response_data && typeof log.response_data === 'object' && 
             (log.response_data.error || log.response_data.message || log.response_data.detail))
          );
          
          if (errorLog) {
            // Extract error message from various possible locations
            let errorMsg = errorLog.error;
            if (!errorMsg && errorLog.response_data) {
              if (typeof errorLog.response_data === 'string') {
                errorMsg = errorLog.response_data;
              } else if (errorLog.response_data.error) {
                errorMsg = errorLog.response_data.error;
              } else if (errorLog.response_data.message) {
                errorMsg = errorLog.response_data.message;
              } else if (errorLog.response_data.detail) {
                errorMsg = errorLog.response_data.detail;
              } else if (errorLog.response_data.title) {
                errorMsg = errorLog.response_data.title;
              }
            }
            
            if (!errorMsg && errorLog.status_code >= 400) {
              errorMsg = `HTTP ${errorLog.status_code}: ${errorLog.status_text || 'Error'}`;
              if (errorLog.endpoint) {
                errorMsg += ` at ${errorLog.endpoint}`;
              }
            }
            
            setDispatcherError(errorMsg || 'Dispatcher service unavailable or error occurred');
          } else if (logs.length > 0) {
            // If there are logs but no explicit error, show a generic message
            setDispatcherError('Dispatcher service unavailable or error occurred');
          } else {
            // No logs found - might be a different type of error
            setDispatcherError('Dispatcher service unavailable. Order may not be eligible for dispatch service.');
          }
        })
        .catch((e) => {
          captureException(e);
          setDispatcherError('Unable to fetch error details. Please check dispatcher logs or contact support.');
        });
    } else if (!order.has_dispatcher_error) {
      setDispatcherError(null);
    }
  }, [order.has_dispatcher_error, order.id]);

  useEffect(() => {
    if (estimatedPickupTs && order.has_delivery && !selectedEstimateOption) {
      setSelectedEstimateOption(deliveryEstimateOptions[0]);
    }
  }, [estimatedPickupTs, order?.has_delivery, deliveryEstimateOptions, selectedEstimateOption]);

  useEffect(() => {
    if (!orderId) {
      return;
    }
    setEstimatedPickupTs(null);
    if (order.state === 0 && order.has_delivery) {
      isRideServiceAvailable(orderId).then((res) => {
        const error = getData(res)?.order?.error;
        if (!error) {
          estimateOrder(orderId).then((res) => {
            setEstimatedPickupTs(getData(res)?.pickupts);
          });
        } else {
          message.error(error);
        }
      });
    }
  }, [order?.has_delivery, orderId, order.state]);

  const handleAcceptOrder = (id) => {
    if (!isScheduled) {
      /** On demand */
      if (Number(selectedEstimateOption?.time || timeEST) <= 0) return;
      if (order.has_delivery && !selectedEstimateOption?.time && !timeEST) {
        return;
      }

      if (isDeliveryEstimateValid()) {
        setAcceptLoading(true);

        updateOrderState(id, {
          state: 1,
          estimated_time: parseInt(selectedEstimateOption?.time || timeEST, 10),
        })
          .then((res) => {
            setAcceptLoading(false);
            handleOrderUpdate(id, getData(res));
            onClose();
          })
          .catch((err) => {
            captureException(err);
            message.error(getHttpErrorMessage(err));
            setAcceptLoading(false);
          });
      } else {
        message.error('Custom time cannot be less than earliest pickup time');
      }
      return;
    }
    /** Scheduled */
    setAcceptLoading(true);
    updateOrderState(id, {
      state: 1,
    })
      .then((res) => {
        setAcceptLoading(false);
        handleOrderUpdate(id, getData(res));
        onClose();
      })
      .catch((err) => {
        captureException(err);
        message.error(getHttpErrorMessage(err));
        setAcceptLoading(false);
      });

    function isDeliveryEstimateValid() {
      /** If it's delivery type, customEstimate should be less than earliest pickup time*/
      if (
        order?.has_delivery &&
          Number(selectedEstimateOption?.time || timeEST) >=
            dayjs(estimatedPickupTs).diff(dayjs(), 'minute')
      ) {
        /** Delivery type */
        return true;
      }
      if (!order.has_delivery) {
        /** Pickup type */
        return true;
      }
      return false;
    }
  };

  const handleRejectOrder = (id) => {
    setRejectLoading(true);
    updateOrderState(id, {
      state: 2,
    })
      .then((res) => {
        setRejectLoading(false);
        handleOrderUpdate(id, getData(res));
        onClose();
      })
      .catch((err) => {
        captureException(err);
        message.error(getHttpErrorMessage(err));
        setRejectLoading(false);
      });
  };

  const handleApproveOrder = (id) => {
    setApproveLoading(true);
    approveOrder(id)
      .then((res) => {
        handleOrderUpdate(id, getData(res));
        onClose();
      })
      .catch((err) => {
        captureException(err);
        message.error(getHttpErrorMessage(err));
      })
      .finally(() => {
        setApproveLoading(false);
      });
  };

  const handleOrderReady = (id) => {
    setReadyLoading(true);
    updateOrderState(id, {
      state: 4,
    })
      .then((res) => {
        setReadyLoading(false);
        onClose();
      })
      .catch((err) => {
        captureException(err);
        message.error(getHttpErrorMessage(err));
        setReadyLoading(false);
      });
  };

  const isEstimatedTimePassed = (time) => {
    const remainingTime = dayjs(time).diff(dayjs(), 'minute');

    if (remainingTime && remainingTime < 0) {
      return true;
    }
    return false;
  };

  const showEStSelection = !isScheduled && setting?.timeEstSelection;

  const handleUpdateOrder = (id, time) => {
    const minutes = parseInt(time, 10);
    const currentEstimate = orderTime || order?.estimate;
    if (currentEstimate && minutes < 0) {
      const newEstimate = dayjs(currentEstimate).add(minutes, 'minute');
      if (newEstimate.isBefore(dayjs())) {
        message.error('Updated time cannot be earlier than now');
        return;
      }
    }
    setTimeConfirmLoading(true);
    updateOrderState(id, {
      estimated_time: minutes,
    })
      .then((res) => {
        setOrderTime(getData(res).estimate);
        handleOrderUpdate(id, getData(res));
        setTimeConfirmLoading(false);
        setShowTimeModal(false);
      })
      .catch((err) => {
        captureException(err);
        message.error(getHttpErrorMessage(err));
        setTimeConfirmLoading(false);
        setShowTimeModal(false);
      });
  };

  const handleOrderDeliver = (id) => {
    setDeliverLoading(true);
    updateOrderState(id, {
      state: orderStatus.DELIVERED,
    })
      .then((res) => {
        setDeliverLoading(false);
        handleOrderUpdate(id, getData(res));
        onClose();
      })
      .catch((err) => {
        captureException(err);
        setDeliverLoading(false);
      });
  };

  const handlePushToPosHub = () => {
    if (posHubLoading || posHubAwaitingIntegration || posHubOrderId) {
      return;
    }
    if (order.pos_dispatch_status === 'queued') {
      const scheduledFor = order.scheduled_for ? dayjs(order.scheduled_for).format('MMM D, h:mm A') : null;
      const sendsAt = order.pos_sends_at ? formatPosTime(order.pos_sends_at) : null;
      modal.confirm({
        title: 'Send this order to the POS now?',
        content: scheduledFor && sendsAt
          ? `This order is scheduled for ${scheduledFor} and is set to go to the POS around ${sendsAt}. Sending now puts it in the kitchen early.`
          : 'This order is being held from the POS until closer to its scheduled time. Sending now puts it in the kitchen early.',
        okText: 'Send now',
        cancelText: 'Keep queued',
        onOk: pushToPosHub,
      });
      return;
    }
    pushToPosHub();
  };

  const pushToPosHub = () => {
    setPosHubLoading(true);
    return pushOrderToPosHub(orderId)
      .then((res) => {
        if (res.poshub_order_id) {
          setPosHubOrderId(res.poshub_order_id);
        } else {
          setPosHubAwaitingIntegration(true);
        }
        message.success(
          res.already_pushed
            ? 'Order was already placed on POS'
            : 'Order pushed to POS successfully',
        );
        getOrderDetail();
      })
      .catch((err) => {
        message.error(getHttpErrorMessage(err));
        getOrderDetail();
      })
      .finally(() => setPosHubLoading(false));
  };

  const handleRepush = () => {
    if (!repushReason.trim()) return;
    setRepushLoading(true);
    repushOrderToPosHub(orderId, repushReason)
      .then(() => {
        message.success('Order re-submitted to POS successfully');
        setRepushModalOpen(false);
        setRepushReason('');
        setPosHubAwaitingIntegration(true);
        handleOrderUpdate(orderId);
        return getOrderDetailByOrderId(orderId)
          .then((res) => {
            const detailData = getData(res);
            if (detailData && detailData.id) {
              setOrderDetail(detailData);
            }
          })
          .catch((e) => {
            captureException(e);
          });
      })
      .catch((err) => {
        message.error(getHttpErrorMessage(err) || 'Failed to re-submit order');
      })
      .finally(() => setRepushLoading(false));
  };

  const handleAddNote = () => {
    if (!noteText.trim()) return;
    setNoteLoading(true);
    http.post(`v1/orders/${orderId}/notes/`, { message: noteText })
      .then(() => {
        message.success('Note added');
        setNoteModalOpen(false);
        setNoteText('');
        return getOrderDetailByOrderId(orderId);
      })
      .then((updated) => setOrderDetail(updated))
      .catch((err) => {
        message.error(getHttpErrorMessage(err) || 'Failed to add note');
      })
      .finally(() => setNoteLoading(false));
  };

  const isMigratedData = useMemo(
    () => order.migration_id &&
      order.migration_driver !== 'MealMe' &&
      order.migration_driver !== 'GFOE' &&
      order.migrated_json_order_details?.items?.length,
    [order],
  );

  const orderItems = useMemo(
    () => (isMigratedData && Array.isArray(order.migrated_json_order_details?.items)
      ? order.migrated_json_order_details?.items?.map((item) => ({
        ...item,
        total_value: getItemValue({ value: item.value, migrationDriver: order.migration_driver }),
      }))
      : order.order_items),
    [order, isMigratedData],
  );

  const transformDetailLabel = (label) => {
    const capitalizeLabel = (text) => text.charAt(0).toUpperCase() + text.slice(1);
    return capitalizeLabel(label.split('_').join(' '));
  };

  const findDriver = () => {
    setCanRequestDispatcher(false);
    createDispatcherJob({ id: orderId, organizationId })
      .then((result) => {
        message.open({
          type: 'success',
          content: 'Successfully create a job for dispatcher',
        });
      })
      .catch((e) => {
        captureException(e);
        message.open({
          type: 'error',
          content: 'Something went wrong during request a driver!',
        });
        setCanRequestDispatcher(true);
      });
  };

  const showFindDriverButton = useMemo(
    () => order.state === orderStatus.NEEDS_APPROVAL ||
      order.state === orderStatus.NEW ||
      (order.state === orderStatus.IN_PROGRESS && canRequestDispatcher),
    [order, canRequestDispatcher],
  );

  const handleUpdateOrderState = () => {
    setLoading(true);
    getOrderDetail();
  };

  function renderAlerts() {
    return (
      <>
        {order.has_dispatcher_error && (
          <Alert
            message={
              <div>
                <div style={{ marginBottom: 8, fontWeight: 500 }}>
                  Dispatcher Error
                </div>
                {dispatcherError ? (
                  <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>
                    {dispatcherError}
                  </div>
                ) : (
                  <div style={{ fontSize: 13 }}>
                    Loading error details...
                  </div>
                )}
                <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
                  To ensure a resolution, please contact {WL_CONFIG.name} or check the Dispatcher Debug Panel.
                </div>
              </div>
            }
            type="error"
            showIcon
            closable
            className="mb-4 mx-4"
          />
        )}
        {order.state === orderStatus.NEEDS_APPROVAL && (
          <Alert
            message="Auto sending has been turned off for this merchant. Send order to merchant so they can view it on their tablet."
            type="warning"
            showIcon
            closable
            className="mb-4 mx-4"
          />
        )}
        {order.has_integration_error && order.pos_dispatch_status !== 'failed' && !isEzCaterOrderView && (
          <Alert
            message="Order not processed. Please reorder using the provided link or call the merchant directly.            "
            type="warning"
            showIcon
            closable
            className="mb-4 mx-4"
          />
        )}
        <ConfigProvider theme={{ token: { colorSuccess: '#52c41a', colorWarning: '#fa8c16' } }}>
          {order.pos_dispatch_status === 'queued' && (
            <Alert
              message={queuedPosDetailMessage(
                order.pos_sends_at ? formatPosTime(order.pos_sends_at) : null,
              )}
              type="warning"
              showIcon
              className="mb-4 mx-4"
            />
          )}
          {order.pos_dispatch_status === 'sent' && (
            <Alert
              message={
                order.pos_sent_at
                  ? `Sent to POS at ${formatPosTime(order.pos_sent_at)}.`
                  : 'Sent to POS.'
              }
              type="success"
              showIcon
              className="mb-4 mx-4"
            />
          )}
        </ConfigProvider>
        {order.pos_dispatch_status === 'failed' && (
          <Alert
            message="Failed to send to POS"
            type="error"
            showIcon
            className="mb-4 mx-4"
          />
        )}
        {isEzCaterOrderView && (
          <Alert
            message="External ezCater order — view only. Accept, edit, and POS actions are disabled until lifecycle updates ship (Ticket 9)."
            type="info"
            showIcon
            className="mb-4 mx-4"
          />
        )}
      </>
    );
  }

  function renderCustomerInfo() {
    return (
      <Row divider={false} className="flex-col md:flex-row items-start">
        <Space direction="horizontal">
          {order.is_gift
            ? `Sender: ${order.gift_sender_name || order.customer?.name}`
            : order.customer?.name}
          {[PaymentTypes.CASH, PaymentTypes.ALTERNATE_PAYMENT].includes(
            order.payment_type,
          ) && (
            <Typography.Text type="secondary" className="text-xs">
              {order.payment_type === PaymentTypes.CASH
                ? '(Cash Payment)'
                : `(Alt Payment‍‍ ${order.alternate_payment_number})`}
            </Typography.Text>
          )}
        </Space>

        <Space size={isDesktop ? 'large' : 'small'} className="flex-col md:flex-row items-start">
          <JumpLink href={`tel:${order.customer?.phone}`} ellipsis={false}>
            <PhoneOutlined className="mr-1" />
            {order.customer?.phone}
          </JumpLink>
          <JumpLink href={`mailto:${order.customer?.email}`} maxWidth={200}>
            <MailOutlined className="mr-1" />
            {order.customer?.email}
          </JumpLink>
        </Space>

        {(order.external_order_details?.tracking_link || 
          order.dispatcher_dropoff_tracking_link || 
          order.dispatcher_pickup_tracking_link) && (
          <JumpLink
            target="_blank"
            rel="noreferrer noopener"
            href={order.external_order_details?.tracking_link || 
                  order.dispatcher_dropoff_tracking_link || 
                  order.dispatcher_pickup_tracking_link}
          >
            Track Order
          </JumpLink>
        )}
      </Row>
    );
  }

  function renderRecipientInfo() {
    return (
      order.recipient_number && (
        <Row>
          <Value
            label="Recipient info"
            value={order.recipient_name}
          />
          <JumpLink href={`tel:${order.recipient_number}`}>
            <br />
            <PhoneOutlined className="mr-1" />
            {order.recipient_number}
          </JumpLink>
        </Row>
      )
    );
  }

  function renderAddressInfo() {
    return (
      order.has_delivery && (
        <Row className="flex-col md:flex-row">
          <div className="flex-1">
            <Value
              label="Address"
              value={order.address?.address}
              direction="horizontal"
              className="items-start"
            />
          </div>

          <JumpLink
            target="_blank"
            rel="noreferrer"
            href={`https://google.com/maps?q=${order.address?.location?.lat},${order.address?.location?.lng}`}
            className="justify-self-end"
          >
            Direction
          </JumpLink>
        </Row>
      )
    );
  }

  function renderScheduleInfo() {
    return (
      isScheduled && (
        <Row>
          <Value
            direction="horizontal"
            label={(
              <>
                <CalendarOutlined className="mr-2" />
                Scheduled for
                {' '}
              </>
            )}
          >
            {
              order.is_catering ? (
                <Typography.Text>
                  {dayjs(order.requested_pickup_time || order.scheduled_for || order.estimate).format('ddd MMM DD, LT')}
                </Typography.Text>
              ) : (
                <Typography.Text>
                  {dayjs(order.scheduled_for || order.estimate).format('ddd MMM DD, LT')}
                  -
                  {dayjs(order.scheduled_for || order.estimate).add(0.5, 'hour').format('LT')}
                </Typography.Text>
              )
            }
          </Value>
        </Row>
      )
    );
  }

  function renderDeliveryInfo() {
    if (!order.has_delivery) {
      return null;
    }

    return (
      <Flex vertical gap={16}>
        <Row>
          <DeliveryTypeSelection
            order={order}
            refresh={getOrderDetail}
          />
        </Row>
        <Row className="flex-col md:flex-row gap-4" divider={false}>
          <Value
            label="Delivery status"
            value={
              RIDE_STATUS.find((s) => s.id === order.delivery_state)?.title ||
              '—'
            }
          />

          {order.dispatcher_trace_id && (
            <Value
              label={order.dispatcher_type === 'fulflld' ? 'FULFLLD Order ID' : 'Expedite Order ID'}
              value={
                order.dispatcher_type === 'fulflld' && order.dispatcher_trace_id?.length > 16 ? (
                  <Space size="small">
                    <Typography.Text style={{ fontFamily: 'monospace' }}>
                      {order.dispatcher_trace_id.slice(0, 12)}…
                    </Typography.Text>
                    <Tooltip title="Copy full ID">
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => {
                          if (!navigator.clipboard?.writeText) {
                            message.warning('Clipboard not available');
                            return;
                          }
                          navigator.clipboard
                            .writeText(order.dispatcher_trace_id)
                            .then(
                              () => message.success('Copied to clipboard'),
                              () => message.error('Copy failed')
                            );
                        }}
                        aria-label="Copy order ID"
                      />
                    </Tooltip>
                  </Space>
                ) : (
                  order.dispatcher_trace_id
                )
              }
            />
          )}

          <Value
            label="Pickup ETA"
            value={
              order?.estimated_pickup_time
                ? `${dayjs(order?.estimated_pickup_time).format(
                  'MMM DD, hh:mm A',
                )} (${dayjs(order?.estimated_pickup_time).fromNow()})`
                : '—'
            }
          />
          <Value label="Driver">
            <Typography.Text>
              {order.driver_name ? order.driver_name : '—'}
              {' '}
              {order.driver_phone && (
                <a
                  className="driver-phone-link"
                  href={`tel:${order.driver_phone}`}
                >
                  <span>Call</span>
                </a>
              )}
            </Typography.Text>
          </Value>

          {((order.dispatcher_type === 'fulflld' && order.dispatcher_trace_id) ||
            (order.dispatcher_external_link && order.dispatcher_type !== 'expedite')) && (
            <Value label="Dispatch">
              <JumpLink
                href={
                  order.dispatcher_type === 'fulflld'
                    ? 'https://app.fulflld.com/'
                    : order.dispatcher_external_link
                }
                rel="noopener noreferrer"
                target="_blank"
              >
                Go to Dispatch
              </JumpLink>
            </Value>
          )}

          {(order.external_order_details?.tracking_link ||
            order.dispatcher_dropoff_tracking_link ||
            order.dispatcher_pickup_tracking_link) && (
            <Value label="Tracking">
              <JumpLink
                href={order.external_order_details?.tracking_link ||
                      order.dispatcher_dropoff_tracking_link ||
                      order.dispatcher_pickup_tracking_link}
                rel="noopener noreferrer"
                target="_blank"
              >
                Track Order
              </JumpLink>
            </Value>
          )}

          {showFindDriverButton && (
            <Button disabled={!canRequestDispatcher} onClick={findDriver}>
              {canRequestDispatcher ? 'Find a driver' : 'Requested'}
            </Button>
          )}
        </Row>

        {isAdmin
          && order.delivery_type === 'omni_dispatch'
          && (order.expedite_delivery_fee != null || order.expedite_tip != null) && (
            <div className="w-full px-5 lg:px-8">
              <Value label="Expedite cost Component" className="items-start max-w-md">
                <Flex
                  vertical
                  gap={6}
                  className="w-full max-w-sm rounded-md border px-3 py-2"
                  style={{
                    borderColor: 'var(--ant-color-border-secondary, rgba(0,0,0,0.08))',
                    background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                  }}
                >
                  {order.expedite_delivery_fee != null && (
                    <Flex justify="space-between" gap={12} align="baseline" wrap={false}>
                      <Typography.Text type="secondary" className="shrink-0">
                        Delivery fee
                      </Typography.Text>
                      <Typography.Text strong className="tabular-nums text-right">
                        {formatAmount(order.expedite_delivery_fee)}
                      </Typography.Text>
                    </Flex>
                  )}
                  {order.expedite_tip != null && (
                    <Flex justify="space-between" gap={12} align="baseline" wrap={false}>
                      <Typography.Text type="secondary" className="shrink-0">
                        Tip
                      </Typography.Text>
                      <Typography.Text strong className="tabular-nums text-right">
                        {formatAmount(order.expedite_tip)}
                      </Typography.Text>
                    </Flex>
                  )}
                </Flex>
              </Value>
            </div>
          )}
      </Flex>
    );
  }

  function renderEstimateInfo() {
    if (isScheduled || order.state === orderStatus.ON_THE_WAY || !setting?.timeSelection) {
      // If the order is scheduled or it's on the way, we cannot update the time
      return null;
    }
    const estimateForDisplay = orderTime ?? order?.estimate;
    const estimatedTimeDisplay = estimateForDisplay
      ? (dayjs(estimateForDisplay).isBefore(dayjs())
        ? 'Now'
        : dayjs(estimateForDisplay).fromNow())
      : '—';
    return (
      <Row>
            <Value
          direction="horizontal"
          label={(
            <>
              {isEstimatedTimePassed(estimateForDisplay) && (
                <WarningOutlined style={{ color: 'red' }} className="mr-2" />
              )}
              Estimated time
            </>
          )}
          value={estimatedTimeDisplay}
        />
        <Button onClick={() => setShowTimeModal(true)}>Update Time</Button>
      </Row>
    );
  }

  function renderItemsInfo() {
    const modifiers = (item) => (item.order_options?.length > 0
      ? item.order_options
      : item.order_modifier);
    return (
      <Row title="Items">
        <List
          className="w-full"
          itemLayout="horizontal"
          size="small"
          dataSource={orderItems || []}
          renderItem={(item, index) => (
            <List.Item>
              <List.Item.Meta
                title={(
                  <div className="flex justify-between">
                    <div>

                      {item.qty && (
                        <>
                          {item.qty}
                          <span className="mx-2">x</span>
                        </>
                      )}
                      {' '}
                      {item.item_name}
                      {' '}
                      {item.size?.trim() && `(${item.size})`}
                      {' '}
                    </div>
                    {formatAmount(item.total_value)}
                  </div>
                )}
                description={(
                  <>
                    {modifiers(item).length > 0 && (
                      <span className="ml-8 block">
                        {(modifiers(item))
                          .map(
                            (m) => `${m.qty > 1 ? m.qty : ''} ${
                              m.modifier_option?.title || m.title
                            } (${m.modifier_option?.price >= 0 ? `$${m.modifier_option?.price}` : m.price})`,
                          )
                          .join(' · ')}

                      </span>
                    )}
                    {item.message && (
                      <Space className="flex ml-8">
                        <MessageOutlined />
                        {item.message}
                      </Space>
                    )}
                    {item.serving_size > 0 && (
                      <Space className="flex ml-8 mt-2" size="small">
                        <InfoCircleOutlined />
                        <span>
                          {renderServingSizeLabel(item.serving_size)}
                        </span>
                      </Space>
                    )}
                  </>

                )}
              />
            </List.Item>
          )}
        />
      </Row>
    );
  }

  function renderNoteInfo() {
    return (
      <>
        {order.admin_note && (
          <Row>
            <Value
              direction="horizontal"
              label="Admin Note"
              value={order.admin_note}
            />
          </Row>
        )}
        {order.delivery_message && (
          <Row>
            <Value
              direction="horizontal"
              label="Customer Note"
              value={order.delivery_message}
            />
          </Row>
        )}
      </>
    );
  }

  function renderFinancialInfo() {
    let fields;
    if (!isAdmin) {
      fields = [
        ['Subtotal', formatAmount(order.food_real_value)],
        ['Tax', formatAmount((order.tax_rate || 0) * order.food_real_value)],
        ['Merchant Net', formatAmount(order.restaurant_net)],
        ['Payment Fees', formatAmount(order.order_nets?.restaurant_fee)],
      ];
    } else {
      fields = [
        ['Total', formatAmount(order.value)],
        ['Subtotal', formatAmount(order.food_value)],
        ['Subtotal Without Markup', formatAmount(order.food_real_value)],
        ['Tax', formatAmount(order.order_nets?.total_tax)],
        ['Merchant Tip', formatAmount(order.order_nets?.restaurant_tip_amount)],
        ['Merchant Net', formatAmount(order.restaurant_net)],
        ['Delivery Fee', formatAmount(order.delivery_fare)],
        ['Customer tip', formatAmount(order.tip)],
        [
          'Tip sent to driver',
          order.dispatcher_tip != null ? formatAmount(order.dispatcher_tip) : '-',
        ],
        ['Driver tip (nets)', formatAmount(order.order_nets?.rideshare_tip_amount)],
        ['Service Fee', formatAmount(order.service_fee)],
        [
          'Coupon',
          order.coupon_amount != null ? formatAmount(order.coupon_amount) : '-',
        ],
        ['Adjustment Amount', formatAmount(order.adjustment_amount)],
        [
          'Payment Fee',
          formatAmount(order.order_nets?.stripe || order.order_nets?.moneris),
        ],
      ];
    }

    const paymentDetails = order.payment?.custom_order_payment ? (
      <Row>
        <Space direction="vertical">
          <Value
            label={(
              <Space>
                <CreditCardOutlined />
                <Typography.Text style={{ color: 'inherit' }}>Payment</Typography.Text>
              </Space>
            )}
            direction="horizontal"
            value={order.payment.custom_order_payment?.custom_payment?.title || 'Custom'}
          />
          <Space className="pl-5" direction="vertical">
            {
              Object.entries(order.payment.custom_order_payment?.meta ?? {}).map(([key, value]) => (
                <Value
                  label={key}
                  value={value}
                  direction="horizontal"
                />
              ))
            }
          </Space>
        </Space>

      </Row>
    ) : null;
    return (
      <>
        {paymentDetails}
        <Row className="items-center mt-4">
          {renderItems()}
        </Row>
      </>

    );

    function renderItems() {
      return (
        <Descriptions
          bordered
          size="small"
          column={1}
          styles={{ content: { whiteSpace: 'nowrap' } }}
          className="overflow-auto"
        >
          {
            isMigratedData && order.migrated_json_order_details?.details ?
              Object.entries(order.migrated_json_order_details.details)?.map(
                ([key, value]) => (
                  <Descriptions.Item key={key} label={transformDetailLabel(key)}>
                    {value}
                  </Descriptions.Item>
                ),
              ) :
              fields.map(([title, value]) => (
                <Descriptions.Item key={title} label={title}>
                  {value}
                </Descriptions.Item>
              ))

          }
        </Descriptions>
      );
    }
  }

  function renderEstimateForm() {
    const options = order.has_delivery ? deliveryEstimateOptions : estimateOptions;

    return (
      showEStSelection && (
        <Row>
          <Value label="Estimated time">
            {!order.has_delivery || estimatedPickupTs ? (
              <Radio.Group
                value={selectedEstimateOption?.id}
                onChange={({ target: { value } }) => setSelectedEstimateOption(
                  options.find((o) => +o.id === +value),
                )}
              >
                {options.map((option) => (
                  <Radio value={option.id}>{option.title}</Radio>
                ))}
              </Radio.Group>
            ) : (
              <Skeleton paragraph={{ rows: 1 }} />
            )}
            <Typography.Text type="secondary">
              Pick a time that accurately estimates how long this order will
              take to prepare.
            </Typography.Text>
          </Value>
        </Row>
      )
    );
  }

  function renderHeadCount() {
    if (order.headcount) {
      return (
        <Row>
          <Value direction="horizontal" label="Headcount" value={`${order.headcount} people`} />
        </Row>
      );
    }
  }

  function renderMerchantInfo() {
    const {
      integration_merchant_link: merchantLink,
      integration_merchant_phone_number: merchantPhone,
    } = order || {};

    const phone = merchantPhone || order.merchant?.phone;
    if (!order.merchant) {
      return null;
    }
    return (
      <>
        <Row>
          <Value label="Merchant name" value={order.merchant?.name} />
          <Space align="end">
            {phone && (
              <Value label="Merchant phone number">
                <JumpLink href={`tel:${phone}`} ellipsis={false} maxWidth="none">
                  <Space>
                    <PhoneOutlined />
                    {formatPhoneNumber(phone)}
                  </Space>
                </JumpLink>
              </Value>
            )}
            {merchantLink && (<JumpLink href={merchantLink}>Ordering link</JumpLink>)}
          </Space>
        </Row>
        <Row className="flex-col md:flex-row mt-4" divider={false}>
          <div className="flex-1">
            <Value label="Merchant type" value={capitalizeMerchantType(order.merchant?.merchant_type)} />
          </div>
          {order.is_catering && (
            <Space size="middle" className="justify-self-end" wrap>
              <Tooltip
                title={
                  canOpenPrepSheet
                    ? null
                    : "You don't have access to Prep Sheets. Ask an admin for Prep & Kitchen access."
                }
              >
                <span style={{ display: 'inline-block' }}>
                  <JumpLink
                    target="_self"
                    ellipsis={false}
                    maxWidth="none"
                    href={`/console/prep-kitchen/prep-sheet?order_id=${order.id}`}
                    style={canOpenPrepSheet ? undefined : { opacity: 0.45, pointerEvents: 'none' }}
                    onClick={(e) => {
                      e.preventDefault();
                      openPrepSheetForOrder();
                    }}
                  >
                    Prep sheet
                  </JumpLink>
                </span>
              </Tooltip>
              {order.has_delivery && (
                <JumpLink
                  role="button"
                  ellipsis={false}
                  maxWidth="none"
                  style={handoffSheetLoading ? { opacity: 0.45, pointerEvents: 'none' } : undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    handleHandoffSheet();
                  }}
                >
                  Handoff sheet
                </JumpLink>
              )}
            </Space>
          )}
        </Row>
      </>
    );
  }

  function renderContent() {
    if (!order.customer || loading) {
      return <Skeleton paragraph={{ rows: 10 }} className="p-4" />;
    }
    return (
      <div
        className="py-4"
        style={{
          // maxHeight: '70vh',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {renderAlerts()}
        {renderCustomerInfo()}
        {renderRecipientInfo()}
        {renderMerchantInfo()}
        {renderAddressInfo()}
        {renderScheduleInfo()}
        {renderHeadCount()}
        {renderDeliveryInfo()}
        {renderEstimateInfo()}
        {renderItemsInfo()}
        {renderNoteInfo()}
        {renderEstimateForm()}
        {renderFinancialInfo()}
        {showTimeModal && (
          <TimeDialogue
            handleCancel={() => setShowTimeModal(false)}
            handleConfirm={(time) => handleUpdateOrder(order?.id ?? orderId, time)}
            loading={timeConfirmLoading}
          />
        )}
        {!actionsDisabled && <SendReceiptSection order={order} />}
        <ChangeLog order={order} />

        {/* ── Order History / Version timeline (all orders) ── */}
        {order.versions?.length > 0 && (
          <div style={{ padding: '0 24px', marginTop: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <Typography.Title level={5} style={{ margin: 0 }}>Order History</Typography.Title>
              {isAdmin && (
                <Button size="small" onClick={() => setNoteModalOpen(true)}>
                  + Add Note
                </Button>
              )}
            </div>
            <Timeline
              items={order.versions.map((version) => ({
                color: {
                  POS_REJECTED: 'orange',
                  ITEMS_UPDATED: 'blue',
                  REPUSH_TO_POS: 'green',
                  MANUAL_NOTE: 'gray',
                }[version.triggered_by] || 'gray',
                children: (
                  <div key={version.version_number}>
                    <Typography.Text strong>
                      {{
                        POS_REJECTED: 'POS Rejected',
                        ITEMS_UPDATED: 'Items Updated',
                        REPUSH_TO_POS: 'Re-submitted to POS',
                        MANUAL_NOTE: 'Note Added',
                      }[version.triggered_by] || version.triggered_by}
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                      {dayjs(version.created_at).format('MMM D, h:mm A')}
                    </Typography.Text>
                    {version.notes.map((note, i) => (
                      <div key={i} style={{ marginTop: 2, paddingLeft: 4 }}>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>{note.author_label}: </Typography.Text>
                        <Typography.Text style={{ fontSize: 12 }}>{note.message}</Typography.Text>
                      </div>
                    ))}
                  </div>
                ),
              }))}
            />
          </div>
        )}

        {/* ── POS rejection warning banner ── */}
        {order.state === orderStatus.REJECTED_BY_RESTAURANT && (
          <div style={{ padding: '0 24px', marginTop: 16 }}>
            <Alert
              type="warning"
              showIcon
              message="Order rejected by POS (Lightspeed)"
              description="Review the order items, make any necessary changes, then use 'Re-submit to POS' to send it back to the restaurant."
            />
          </div>
        )}

        {/* ── Re-submit to POS modal ── */}
        <Modal
          title="Re-submit Order to POS"
          open={repushModalOpen}
          onOk={handleRepush}
          confirmLoading={repushLoading}
          maskClosable={!repushLoading}
          onCancel={() => {
            if (repushLoading) return;
            setRepushModalOpen(false);
            setRepushReason('');
          }}
          okText="Re-submit"
          okButtonProps={{ disabled: !repushReason.trim() || repushLoading }}
        >
          <Typography.Paragraph>
            Provide a reason for the re-submission. This is saved in the order history and visible to your team.
          </Typography.Paragraph>
          <Input.TextArea
            rows={3}
            placeholder="e.g. Removed out-of-stock items, customer requested time change"
            value={repushReason}
            onChange={(e) => setRepushReason(e.target.value)}
            autoFocus
          />
        </Modal>

        {/* ── Add Note modal ── */}
        <Modal
          title="Add Note to Order"
          open={noteModalOpen}
          onOk={handleAddNote}
          confirmLoading={noteLoading}
          onCancel={() => { setNoteModalOpen(false); setNoteText(''); }}
          okText="Save Note"
          okButtonProps={{ disabled: !noteText.trim() }}
        >
          <Input.TextArea
            rows={3}
            placeholder="Internal note — visible to your team only"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            autoFocus
          />
        </Modal>
      </div>
    );
  }

  return (
    <>
      <Drawer
        className="order-overlay-drawer"
        title={(
          <Header
            order={order}
            onClose={onClose}
            loading={loading}
            handleStateChange={handleUpdateOrderState}
            hasAction={!actionsDisabled}
            onDebugClick={() => setDebugPanelOpen(true)}
          />
        )}
        placement="right"
        onClose={() => {
          setOrderDetail({});
          onClose();
        }}
        closeIcon={false}
        open={open}
        width={720}
        styles={{
          body: { padding: '24px 0' },
          footer: {
            position: 'sticky', bottom: 0, background: '#fff', padding: '10px 16px', textAlign: 'right',
          },
        }}
        footer={!actionsDisabled && (loading ? <Skeleton.Input /> : setting?.buttons ?? null)}
      >
        {renderContent()}
      </Drawer>
      
      {/* Dispatcher Debug Panel */}
      <DispatcherDebugPanel
        orderId={orderId}
        open={debugPanelOpen}
        onClose={() => setDebugPanelOpen(false)}
      />
    </>
  );
}

function Value({
  label, value, children, ...props
}) {
  return (
    <Space direction="vertical" {...props}>
      <Typography.Text type="secondary" style={{ wordBreak: 'normal', textTransform: 'capitalize' }}>{label}</Typography.Text>
      {children || <Typography.Text strong>{value}</Typography.Text>}
    </Space>
  );
}

function CustomEstInput({ value, onChange, disabled = false }) {
  const [errorOpen, setErrorOpen] = useState(false);
  return (
    <Space>
      <Popconfirm
        open={errorOpen}
        title="Invalid Time"
        description="Custom time cannot be more than 60 minutes"
        onConfirm={() => setErrorOpen(false)}
        showCancel={false}
      >
        <Input
          type="number"
          value={value}
          onKeyDown={(e) => {
            ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault();
          }}
          onChange={({ target: { value: val } }) => {
            if (val === '' || (val > 0 && val <= 60)) {
              onChange(val);
            } else if (val > 60) {
              onChange(60);
              setErrorOpen(true);
            }
          }}
          disabled={disabled}
          placeholder="Custom time"
        />
      </Popconfirm>
      <span>min</span>
    </Space>
  );
}
function ButtonWrapper({ editOrderButton, cancelOrderButton, children }) {
  return (
    <div className="flex justify-end flex-col sm:flex-row gap-4">
      <div className="flex flex-col sm:flex-row gap-2">
        {editOrderButton || <span />}
        {cancelOrderButton || <span />}
        {children}
      </div>
    </div>
  );
}
OrderOverlay.propTypes = {};

function SendReceiptSection({ order }) {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState();
  const { message } = App.useApp();

  function handleSend() {
    setLoading(true);
    http.post(`/v1/orders/${order.id}/send-email/`, { emails: [email] }).then(() => {
      message.success('Email sent');
    }).catch((e) => {
      message.error(getHttpErrorMessage(e));
    }).finally(() => {
      setLoading(false);
    });
  }
  return (
    <Row className="flex-col items-start">
      <Typography.Title level={4}>
        Send order detail via email
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        By entering your email address you can get this order&apos;s details.
      </Typography.Paragraph>
      <Flex gap={16} style={{ maxWidth: 400 }}>
        <Form onFinish={handleSend}>
          <Flex gap={16}>
            <FormItem
              rules={
                [{
                  required: true,
                  message: 'Please enter your email',
                }]
              }
            >
              <Input type="email" value={email} onChange={({ target: { value } }) => { setEmail(value); }} />
            </FormItem>
            <Button htmlType="submit" loading={loading} disabled={loading}>
              Send
            </Button>
          </Flex>
        </Form>

      </Flex>
    </Row>
  );
}

function DeliveryTypeSelection({
  order,
  refresh,
}) {
  const [deliveryType, setDeliveryType] = useState(order.delivery_type || undefined);
  const [loading, setLoading] = useState(false);
  const { message } = App.useApp();
  let disabled = false;
  let alertMessage = '';
  let alertType = 'info';

  const isEnRouteThirdPartyDelivery =
    ['omni_dispatch', 'fulflld'].includes(deliveryType) &&
    [3, 4].includes(order.delivery_state);

  const isEzCater = isEzCaterOrder(order);

  useEffect(() => {
    setDeliveryType(order.delivery_type || undefined);
  }, [order.delivery_type]);

  function handleDeliveryTypeChange(deliveryType) {
    setDeliveryType(deliveryType);
    setLoading(true);
    http.post(`/v1/orders/${order.id}/delivery-type`, { delivery_type: deliveryType })
      .then(() => {
        message.success('Delivery type updated');
        refresh();
      })
      .catch((e) => {
        message.error(getHttpErrorMessage(e));
        setDeliveryType(order.delivery_type);
      })
      .finally(() => {
        setLoading(false);
        refresh();
      });
  }

  if (order.state === orderStatus.ON_THE_WAY) {
    disabled = true;
    alertMessage = 'You can no longer change the delivery type because your order is on the way.';
  } else if (isEnRouteThirdPartyDelivery && !order.can_change_delivery_to_self_delivery) {
    alertType = 'warning';
    alertMessage = order.self_delivery_unavailable_reason
      || 'A dispatch driver is already en route. Cancel the dispatch task before switching to self delivery.';
  } else if (isEzCater && deliveryType === 'omni_dispatch') {
    alertType = 'warning';
    alertMessage = order.omni_dispatch_unavailable_reason
      || 'This ezCater order must use FULFLLD. Expedite-dispatch is not allowed.';
  } else if (deliveryType === 'self_delivery') {
    alertMessage = 'Self delivery: the restaurant handles delivery; no third-party dispatcher fleet is used for this order.';
  } else if (deliveryType === 'fulflld') {
    alertMessage = "You selected 'FULFLLD', which means orders will be dispatched via FULFLLD.";
  } else if (deliveryType === 'omni_dispatch') {
    alertMessage = "You selected 'Expedite-dispatch', which means a driver will be assigned from the Expedite dispatch service to handle the delivery.";
  }

  const expediteBlockedReason = order.omni_dispatch_unavailable_reason;

  const expediteDisabled =
    disabled ||
    loading ||
    (!order.can_change_delivery_to_omni_dispatch && (deliveryType !== 'omni_dispatch' || isEzCater));
  const expediteTooltipTitle =
    !disabled && expediteDisabled && expediteBlockedReason
      ? expediteBlockedReason
      : null;

  const selfDeliveryBlockedReason = isEzCater
    ? 'ezCater orders must be routed through FULFLLD and cannot use self delivery.'
    : order.self_delivery_unavailable_reason;

  const selfDeliveryDisabled =
    disabled ||
    loading ||
    isEzCater ||
    (!order.can_change_delivery_to_self_delivery && deliveryType !== 'self_delivery');
  const selfDeliveryTooltipTitle =
    !disabled && selfDeliveryDisabled && deliveryType !== 'self_delivery'
      ? (selfDeliveryBlockedReason
        || 'Self delivery is only available when switching from Expedite-dispatch or unset routing. It cannot be selected while the order is already on FULFLLD or in a terminal state.')
      : null;

  const fulflldDisabled =
    disabled ||
    loading ||
    !order.fulflld_configured ||
    (!order.can_change_delivery_to_fulflld && deliveryType !== 'fulflld');
  const fulflldTooltipTitle =
    !disabled && fulflldDisabled && deliveryType !== 'fulflld'
      ? (!order.fulflld_configured
        ? 'FULFLLD is not configured for this organization. Contact support to enable it.'
        : 'FULFLLD cannot be selected for this order in its current routing state.')
      : null;

  return (
    <Flex vertical gap={16}>
      <Value
        direction="horizontal"
        label={
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: '8px' }}>Delivery Type</Typography.Text>
        }
        value={(
          <Radio.Group
            value={deliveryType}
            onChange={(e) => handleDeliveryTypeChange(e.target.value)}
            disabled={disabled || loading}
            optionType="button"
            size="medium"
            loading={loading}
            className="flex items-center justify-center flex-wrap"
          >
            <Tooltip title={selfDeliveryTooltipTitle}>
              <span>
                <Radio.Button
                  value="self_delivery"
                  disabled={selfDeliveryDisabled}
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    fontWeight: 400,
                  }}
                >
                  <Flex align="center" justify="center" gap={4}>
                    {deliveryType === 'self_delivery' && <Check />}
                    <span>Self delivery</span>
                  </Flex>
                </Radio.Button>
              </span>
            </Tooltip>
            <Tooltip title={expediteTooltipTitle}>
              <span>
                <Radio.Button
                  value="omni_dispatch"
                  disabled={expediteDisabled}
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    fontWeight: 400,
                  }}
                >
                  <Flex align="center" justify="center" gap={4}>
                    {deliveryType === 'omni_dispatch' && <Check />}
                    <span>
                      Expedite-dispatch
                    </span>
                  </Flex>
                </Radio.Button>
              </span>
            </Tooltip>
            <Tooltip title={fulflldTooltipTitle}>
              <span>
                <Radio.Button
                  value="fulflld"
                  disabled={fulflldDisabled}
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    fontWeight: 400,
                  }}
                >
                  <Flex align="center" justify="center" gap={4}>
                    {deliveryType === 'fulflld' && <Check />}
                    <span>
                      FULFLLD
                    </span>
                  </Flex>
                </Radio.Button>
              </span>
            </Tooltip>
          </Radio.Group>
        )}
      />
      {alertMessage && (
        <Alert
          message={alertMessage}
          type={alertType}
          showIcon
        />
      )}
    </Flex>
  );
}

export default withGuard(OrderOverlay, [PERMISSIONS.ORDERS], { redirect: true });
